import {
    Button as MuiButton,
    ButtonProps,
    ButtonGroup as MuiButtonGroup,
    ButtonGroupProps,
    IconButton as MuiIconButton,
    IconButtonProps,
    makeStyles,
} from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';
import { theme } from '../../theme/theme';

const useButtonClasses = makeStyles({
    root: {
        transition: `all ${theme.transitions.fast}s`,
        '&.MuiButton-contained': {
            color: theme.colors.white,
            boxShadow: '0 1px 1px 0 rgba(0, 0, 0, 0.14)',
            background: theme.colors.darkBlue[5],
            '&:hover': {
                opacity: 0.9,
            },
        },
        '&.MuiButton-outlined, &.MuiButton-text': {
            color: theme.colors.darkBlue[5],
            borderColor: 'currentColor',
            '&:hover': {
                background: 'rgba(0, 0, 0, 0.05)',
            },
        },
        '&.Mui-disabled': {
            color: theme.colors.neutral[3],
            background: theme.colors.white,
        },
        '&.MuiButton-sizeSmall svg': {
            width: 18,
            height: 18,
        },
        '& svg': {
            width: 20,
            height: 20,
        },
        '&.MuiButton-sizeLarge svg': {
            width: 22,
            height: 22,
        },
    },
});

/**
 * Themed material-ui button.
 *
 * @param props.color enforced type
 */
export const Button = (props: ButtonProps & { color?: never }) => {
    const classes = useButtonClasses();

    return <MuiButton {...props} className={clsx(classes.root, props.className)} />;
};

const useIconButtonClasses = makeStyles({
    root: {
        color: 'inherit',
        '&.MuiIconButton-sizeSmall': {
            padding: 8,
        },
        '&.medium svg': {
            width: 20,
            height: 20,
        },
        '&.MuiIconButton-sizeSmall svg': {
            width: 16,
            height: 16,
        },
    },
});

/**
 * Themed material-ui icon button.
 * This component implements an extra functionality for fixing icon sizes to match the button size, this is
 * automatically done if the `props.size` property is provided.
 * Different from the `Button` component, the component inherits the color from the parent.
 * A color can also be provided by `props.className` or `props.style`
 *
 * @param props.color enforced type
 */
export const IconButton = (props: IconButtonProps & { color?: never }) => {
    const classes = useIconButtonClasses();
    const cls = { [props.size ?? 'medium']: props.size != undefined };

    return <MuiIconButton {...props} className={clsx(classes.root, cls, props.className)} />;
};

const useButtonGroupClasses = makeStyles({
    root: {
        '& > button': {
            minWidth: 0,
            padding: 0,
            borderColor: 'white !important',
            background: theme.colors.neutral[8],
        },
    },
});

/**
 * Button group component.
 * Styles are opinionated, unique for each variant.
 *
 * @param props.color enforced type
 * @param props.buttons internal buttons props
 */
export const ButtonGroup = (props: ButtonGroupProps & { color?: never; buttons?: Parameters<typeof Button>[0][] }) => {
    const { buttons, ...rootProps } = props;
    const classes = useButtonGroupClasses();

    return (
        <MuiButtonGroup {...rootProps} className={clsx(classes.root, rootProps.className)}>
            {props.buttons?.map((props, i) => (
                <Button key={i} {...props} />
            ))}
        </MuiButtonGroup>
    );
};
